<template>
    <page-loading v-if="loading"></page-loading>
    <form-container v-else>
        <page-header class="mb-6 lg:mb-12" data-test="org-unit-header">
            <template v-if="onCorporate()" v-slot:subtitle>
                <a
                    :href="`/settings/teams/${teamId}#/org-units`"
                    class="no-underline! text-sky-600! text-base font-normal"
                >
                    <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft"></font-awesome-icon>
                    All Organizational Units
                </a>
            </template>

            {{ onCorporate() ? orgUnit?.name : 'Details' }}
        </page-header>

        <div
            :class="{
                'grid grid-cols-12': onCorporate(),
                'max-w-(--breakpoint-sm)': !onCorporate(),
            }"
        >
            <page-sidebar
                v-if="onCorporate()"
                appearance="nested"
                :links="sidebarLinks"
                data-test="org-unit-sidebar"
            ></page-sidebar>

            <slot></slot>
        </div>
    </form-container>
</template>
<script setup>
import PageLoading from '~/components/general/PageLoading.vue';
import FormContainer from '~/components/general/FormContainer.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageSidebar from '~/components/general/PageSidebar.vue';
import { watch, computed, onMounted } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { useOrgUnitDetails } from './useOrgUnitDetails';
import { useCurrentGroup } from '~/composables/useCurrentGroup';

const props = defineProps({
    orgUnitId: {
        type: Number,
        required: true,
    },
});

const teamId = Spark.state.currentTeam.id;
const { onCorporate } = useCurrentGroup();

const { mount, orgUnit, loading } = useOrgUnitDetails();

const sidebarLinks = computed(() => {
    const baseLinks = [
        { href: `/settings/teams/${teamId}/org-units/${props.orgUnitId}`, label: 'Details' },
        { href: `/settings/teams/${teamId}/org-units/${props.orgUnitId}/groups`, label: 'Groups' },
        {
            href: `/settings/teams/${teamId}/org-units/${props.orgUnitId}/group-collections`,
            label: 'Collections',
        },
        { href: `/settings/teams/${teamId}/org-units/${props.orgUnitId}/users`, label: 'Users' },
    ];

    // Guard against loading states
    if (loading.value || !orgUnit.value) {
        return baseLinks;
    }

    // Guard against non-billable org units
    if (!orgUnit.value.is_billable) {
        return baseLinks;
    }

    return [
        ...baseLinks,
        {
            href: `/settings/teams/${teamId}/org-units/${props.orgUnitId}/billing-details`,
            label: 'Billing Details',
        },
        {
            href: `/settings/teams/${teamId}/org-units/${props.orgUnitId}/proforma-invoices`,
            label: 'Upcoming Invoices',
        },
        {
            href: `/settings/teams/${teamId}/org-units/${props.orgUnitId}/historical-invoices`,
            label: 'Historical Invoices',
        },
    ];
});

onMounted(() => {
    mount(props.orgUnitId);
});

watch(
    () => props.orgUnitId,
    (newOrgUnitId, oldOrgUnitId) => {
        if (newOrgUnitId && newOrgUnitId !== oldOrgUnitId) {
            mount(newOrgUnitId);
        }
    }
);
</script>
